import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useEffect, useState, type ReactNode } from "react";

/**
 * One thing the client has to provide, in one of two modes.
 *
 * The page used to be two long forms — every field of every kind visible at
 * once, whether or not it was already answered. Nothing told the client what
 * was left, and re-reading a filled-in form to find the one empty field is the
 * work we were asking of them.
 *
 * So a block starts in **write** mode while it is unanswered, and flips to
 * **read** mode — a one-line summary plus a discreet "modifier" — as soon as it
 * is dealt with. What is left to do is then simply what is still expanded.
 *
 * Dealt with is not the same as filled. Every optional block here can be
 * answered with nothing at all, so its content can't say whether the client
 * looked at it: saving it empty has to count, otherwise the page keeps asking a
 * question that has already been answered. Hence `answered`, which the request
 * remembers, alongside `filled`, which only decides the tick and the summary.
 *
 * `children` is a render prop so the form inside can close the block itself,
 * after a save that succeeded (and only then).
 */
export function EditableBlock({
  title,
  description,
  filled,
  answered,
  optional,
  summary,
  readOnly,
  children,
}: {
  title: string;
  /** Shown in write mode only — in read mode the summary says it better. */
  description?: string;
  filled: boolean;
  /**
   * Saved at least once, whether or not anything was put in it. Collapses the
   * block without claiming it holds an answer.
   */
  answered?: boolean;
  /**
   * Optional blocks never open on their own: an empty one is a valid answer,
   * so opening it would show work that isn't.
   */
  optional?: boolean;
  summary?: ReactNode;
  /**
   * The file is closed (see `isNumberRequestValidated`): the block is its
   * summary and nothing else — no form, no way back into one. A block with
   * nothing to read disappears entirely rather than announce that a question
   * nobody can answer any more was left empty.
   */
  readOnly?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const { t } = useTranslation("numberRequests");

  const done = filled || Boolean(answered);

  const [editing, setEditing] = useState(!done && !readOnly);

  // Changed elsewhere (a document removed, a kind switch re-seeding the
  // request) re-decides the mode: the block always reflects the request, not
  // the last click.
  useEffect(() => {
    setEditing(!done && !readOnly);
  }, [done, optional, readOnly]);

  if (readOnly && !summary) return null;

  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        filled && !editing && "border-primary/40 bg-primary/5",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            {filled && (
              <CheckCircleIcon
                className="text-primary h-4 w-4 shrink-0"
                weight="fill"
              />
            )}
            <p className="text-sm font-medium">{title}</p>
            {optional && !filled && (
              <span className="text-muted-foreground text-xs">
                {t("numberRequests.block.optional")}
              </span>
            )}
          </div>

          {editing && description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>

        {/* An empty required block has no read mode to fall back to, so it
            offers no way to close itself — collapsing it would only hide work
            that still has to be done. */}
        {!readOnly && (!editing || done || optional) && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="text-muted-foreground h-auto shrink-0 p-0 text-sm font-normal"
            onClick={() => setEditing(!editing)}
          >
            {editing
              ? t("numberRequests.block.cancel")
              : done
                ? t("numberRequests.block.edit")
                : t("numberRequests.block.fill")}
          </Button>
        )}
      </div>

      <div className="mt-3">
        {editing ? (
          children(() => setEditing(false))
        ) : summary ? (
          summary
        ) : (
          <p className="text-muted-foreground text-sm">
            {t("numberRequests.block.empty")}
          </p>
        )}
      </div>
    </div>
  );
}
